import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { showToast } from './toastSlice';
import axiosInstance from './../../config/axios';
import { getCoins } from './coinsSlice';


export const getPublicGasEstimate = createAsyncThunk('wallet/getPublicGasEstimate', async ({ tokenId, amount, recipientAddress }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance({
      method: 'POST',
      url: '/wallet/public/send-tokens-fee-estimate',
      data: { tokenId, amount, recipientAddress },
    });
    return response.data;
  } catch (error) {
    console.error('Error getting gas estimate:', error);
    return rejectWithValue(error.response.data);
  }
});

export const getPrivateGasEstimate = createAsyncThunk('wallet/getPrivateGasEstimate', async ({ tokenId, amount, recipientAddress }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance({
      method: 'POST',
      url: '/wallet/private/send-tokens-fee-estimate',
      data: { tokenId, amount, recipientAddress },
    });
    return response.data;
  } catch (error) {
    console.error('Error getting private gas estimate:', error);
    return rejectWithValue(error.response.data);
  }
});

export const sendPublicToken = createAsyncThunk('wallet/sendPublicToken', async ({ tokenId, amount, recipientAddress }, { rejectWithValue, dispatch }) => {
  try {
    const response = await axiosInstance({
      method: 'POST',
      url: '/wallet/public/send-tokens',
      data: { tokenId, amount, recipientAddress },
    });

    dispatch(showToast({ msg: response.data.message, state: 'success' }));
    return response.data;
  } catch (error) {
    console.error('Error sending token:', error);
    return rejectWithValue(error.response.data);
  }
});

// Add new thunks for transaction history with pagination
export const getTransactionHistory = createAsyncThunk('wallet/getTransactionHistory', async ({ coinId, mode = 'public', page = 1, limit = 10 }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance({
      method: 'GET',
      url: `/wallet/${mode}/history/${coinId}`,
      params: { page, limit }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching transaction history:', error);
    return rejectWithValue(error.response?.data || { message: 'Failed to fetch transaction history' });
  }
});

export const unshieldTokens = createAsyncThunk('wallet/unshield', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance({
      method: 'POST',
      url: '/wallet/unshield',
      data: payload // Send entire payload including gasLimit and gasPrice
    });
    return response.data;
  } catch (error) {
    console.error('Error unshielding tokens:', error);
    return rejectWithValue(error.response?.data || { message: 'Failed to unshield tokens' });
  }
});
export const approveToken = createAsyncThunk('wallet/approveToken', async ({ tokenId, amount }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance({
      method: 'POST',
      url: '/wallet/approve-token',
      data: { tokenId, amount }
    });
    return response.data;
  } catch (error) {
    console.error('Error approving token:', error);
    return rejectWithValue(error.response?.data || { message: 'Failed to approve token' });
  }
});

export const getShieldEstimate = createAsyncThunk('wallet/shieldEstimate', async ({ tokenId, amount }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance({
      method: 'POST',
      url: '/wallet/shield/estimate',
      data: { tokenId, amount }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting shield estimate:', error);
    return rejectWithValue(error.response?.data || { message: 'Failed to get shield estimate' });
  }
});

export const getUnshieldEstimate = createAsyncThunk('wallet/unshieldEstimate', async ({ tokenId, amount }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance({
      method: 'POST',
      url: '/wallet/unshield/estimate',
      data: { tokenId, amount }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting unshield estimate:', error);
    return rejectWithValue(error.response?.data || { message: 'Failed to get unshield estimate' });
  }
});

export const shieldTokens = createAsyncThunk('wallet/shield', async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance({
      method: 'POST',
      url: '/wallet/shield',
      data: payload // Send entire payload including gasLimit and gasPrice
    });
    return response.data;
  } catch (error) {
    console.error('Error shielding tokens:', error);
    return rejectWithValue(error.response?.data || { message: 'Failed to shield tokens' });
  }
});

export const privateTransfer = createAsyncThunk(
  'wallet/privateTransfer',
  async ({ tokenId, amount, recipientAddress, senderAddress }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: '/wallet/private/send-tokens',
        data: { tokenId, amount, recipientAddress }
      });
      return response.data;
    } catch (error) {
      console.error('Error in Private transfer:', error);
      return rejectWithValue(error.response?.data || { message: 'Private transfer failed. Please try again.' });
    }
  }
);

export const resetErrorState = createAsyncThunk('wallet/resetErrorState', async (_, { dispatch }) => {
  dispatch(walletSlice.actions.clearShieldError());
  dispatch(walletSlice.actions.clearUnshieldError());
  dispatch(walletSlice.actions.clearPrivateTransferError());
  dispatch(walletSlice.actions.clearApproveError());
});

export const syncWallet = createAsyncThunk('wallet/sync', async (mode, { rejectWithValue, dispatch }) => {
  try {
    const response = await axiosInstance({
      method: 'POST',
      url: '/wallet/balance/sync',
    });
    // Refresh coins after successful sync
    if (mode) {
      await dispatch(getCoins(mode));
    }
    return response.data;
  } catch (error) {
    console.error('Error syncing wallet:', error);
    return rejectWithValue(error.response?.data || { message: 'Failed to sync wallet' });
  }
});

const walletSlice = createSlice({
  name: 'wallet',
  initialState: {
    loading: false,
    error: null,
    txHash: undefined,
    activity: {
      address: '',
      chain: '',
      token: '',
      transactions: [],
    },
    // Transaction history with pagination
    transactionHistory: {
      loading: false,
      error: null,
      address: '',
      chain: '',
      transactions: [],
      pagination: {
        page: 1,
        limit: 10,
        totalRecords: 0,
        totalPages: 0
      }
    },
    shieldLoading: false,
    shieldError: null,
    shieldTxHash: undefined,
    shieldEstimate: null,
    shieldEstimateLoading: false,
    shieldEstimateError: null,
    approveLoading: false,
    approveError: null,
    approveSuccess: false,
    unshieldLoading: false,
    unshieldError: null,
    unshieldTxHash: undefined,
    unshieldEstimate: null,
    unshieldEstimateLoading: false,
    unshieldEstimateError: null,
    // Private transfer state
    privateTransferLoading: false,
    privateTransferError: null,
    privateTransferTxHash: undefined,
    // Gas estimate for public send
    publicGasEstimateLoading: false,
    publicGasEstimate: null,
    publicGasEstimateError: null,
    // Gas estimate for private send
    privateGasEstimateLoading: false,
    privateGasEstimate: null,
    privateGasEstimateError: null,
    // Sync state
    syncStatus: 'idle', // 'idle' | 'syncing' | 'synced'
    lastSyncAt: null,
  },
  reducers: {
    setloading: (state, action) => {
      state.loading = true;
    },
    clearShieldError: (state) => {
      state.shieldError = null;
    },
    clearUnshieldError: (state) => {
      state.unshieldError = null;
    },
    clearPrivateTransferError: (state) => {
      state.privateTransferError = null;
    },
    clearShieldTxHash: (state) => {
      state.shieldTxHash = undefined;
    },
    clearUnshieldTxHash: (state) => {
      state.unshieldTxHash = undefined;
    },
    clearShieldEstimate: (state) => {
      state.shieldEstimate = null;
      state.shieldEstimateError = null;
    },
    clearUnshieldEstimate: (state) => {
      state.unshieldEstimate = null;
      state.unshieldEstimateError = null;
    },
    clearApproveError: (state) => {
      state.approveError = null;
    },
    resetApproveState: (state) => {
      state.approveLoading = false;
      state.approveError = null;
      state.approveSuccess = false;
    },
    resetErrorState: (state) => {
      state.shieldError = null;
      state.unshieldError = null;
      state.privateTransferError = null;
      state.approveError = null;
      state.error = null;
      state.publicGasEstimateError = null;
      state.privateGasEstimateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendPublicToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendPublicToken.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
      })
      .addCase(sendPublicToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload?.message || action.error?.message || 'Failed to send tokens';
      })
      // Unshield tokens cases
      .addCase(unshieldTokens.pending, (state) => {
        state.unshieldLoading = true;
        state.unshieldError = null;
      })
      .addCase(unshieldTokens.fulfilled, (state, action) => {
        state.unshieldTxHash = action.payload.data?.transactionHash || action.payload.data?.hash;
        state.unshieldError = null;
        state.unshieldLoading = false;
      })
      .addCase(unshieldTokens.rejected, (state, action) => {
        state.unshieldLoading = false;
        state.unshieldError = action.payload?.error || action.payload?.message || action.error?.message || 'Failed to unshield tokens';
      })
      // Approve token cases
      .addCase(approveToken.pending, (state) => {
        state.approveLoading = true;
        state.approveError = null;
        state.approveSuccess = false;
      })
      .addCase(approveToken.fulfilled, (state, action) => {
        state.approveLoading = false;
        state.approveError = null;
        state.approveSuccess = true;
      })
      .addCase(approveToken.rejected, (state, action) => {
        state.approveLoading = false;
        state.approveError = action.payload?.error || action.payload?.message || action.error?.message || 'Failed to approve token';
        state.approveSuccess = false;
      })
      // Shield estimate cases
      .addCase(getShieldEstimate.pending, (state) => {
        state.shieldEstimateLoading = true;
        state.shieldEstimateError = null;
      })
      .addCase(getShieldEstimate.fulfilled, (state, action) => {
        state.shieldEstimate = action.payload.data;
        state.shieldEstimateError = null;
        state.shieldEstimateLoading = false;
      })
      .addCase(getShieldEstimate.rejected, (state, action) => {
        state.shieldEstimateLoading = false;
        state.shieldEstimateError = action.payload?.error || action.payload?.message || action.error?.message || 'Failed to get shield estimate';
      })
      // Unshield estimate cases
      .addCase(getUnshieldEstimate.pending, (state) => {
        state.unshieldEstimateLoading = true;
        state.unshieldEstimateError = null;
      })
      .addCase(getUnshieldEstimate.fulfilled, (state, action) => {
        state.unshieldEstimate = action.payload.data;
        state.unshieldEstimateError = null;
        state.unshieldEstimateLoading = false;
      })
      .addCase(getUnshieldEstimate.rejected, (state, action) => {
        state.unshieldEstimateLoading = false;
        state.unshieldEstimateError = action.payload?.error || action.payload?.message || action.error?.message || 'Failed to get unshield estimate';
      })
      // Shield tokens cases
      .addCase(shieldTokens.pending, (state) => {
        state.shieldLoading = true;
        state.shieldError = null;
      })
      .addCase(shieldTokens.fulfilled, (state, action) => {
        state.shieldTxHash = action.payload.data?.transactionHash || action.payload.data?.hash;
        state.shieldError = null;
        state.shieldLoading = false;
      })
      .addCase(shieldTokens.rejected, (state, action) => {
        state.shieldLoading = false;
        state.shieldError = action.payload?.error || action.payload?.message || action.error?.message || 'Failed to shield tokens';
      })
      // Private transfer cases
      .addCase(privateTransfer.pending, (state) => {
        state.privateTransferLoading = true;
        state.privateTransferError = null;
      })
      .addCase(privateTransfer.fulfilled, (state, action) => {
        state.privateTransferTxHash = action.payload.transactionHash || action.payload.hash;
        state.privateTransferError = null;
        state.privateTransferLoading = false;
      })
      .addCase(privateTransfer.rejected, (state, action) => {
        state.privateTransferLoading = false;
        state.privateTransferError = action.payload?.error || action.payload?.message || action.error?.message || 'Private transfer failed';
      })
      // Public gas estimate cases
      .addCase(getPublicGasEstimate.pending, (state) => {
        state.publicGasEstimateLoading = true;
        state.publicGasEstimateError = null;
      })
      .addCase(getPublicGasEstimate.fulfilled, (state, action) => {
        state.publicGasEstimate = action.payload.data;
        state.publicGasEstimateError = null;
        state.publicGasEstimateLoading = false;
      })
      .addCase(getPublicGasEstimate.rejected, (state, action) => {
        state.publicGasEstimateLoading = false;
        state.publicGasEstimateError = action.payload?.error || action.payload?.message || action.error?.message || 'Failed to get public gas estimate';
      })
      // Sync wallet cases
      .addCase(syncWallet.pending, (state) => {
        state.syncStatus = 'syncing';
      })
      .addCase(syncWallet.fulfilled, (state, action) => {
        state.syncStatus = 'synced';
        state.lastSyncAt = new Date().toISOString();
        // If the API returns status/lastSyncAt, use those instead
        if (action.payload?.data?.status) {
          state.syncStatus = action.payload.data.status;
        }
        if (action.payload?.data?.lastSyncAt) {
          state.lastSyncAt = action.payload.data.lastSyncAt;
        }
      })
      .addCase(syncWallet.rejected, (state, action) => {
        state.syncStatus = 'idle';
        console.error('Sync failed:', action.payload);
      });
    // Private gas estimate cases
    builder
      .addCase(getPrivateGasEstimate.pending, (state) => {
        state.privateGasEstimateLoading = true;
        state.privateGasEstimateError = null;
      })
      .addCase(getPrivateGasEstimate.fulfilled, (state, action) => {
        state.privateGasEstimate = action.payload.data;
        state.privateGasEstimateError = null;
        state.privateGasEstimateLoading = false;
      })
      .addCase(getPrivateGasEstimate.rejected, (state, action) => {
        state.privateGasEstimateLoading = false;
        state.privateGasEstimateError = action.payload?.error || action.payload?.message || action.error?.message || 'Failed to get private gas estimate';
      })
      // Transaction history cases
      .addCase(getTransactionHistory.pending, (state) => {
        state.transactionHistory.loading = true;
        state.transactionHistory.error = null;
      })
      .addCase(getTransactionHistory.fulfilled, (state, action) => {
        state.transactionHistory.loading = false;
        state.transactionHistory.error = null;
        // Handle different response structures
        if (action.payload.success && action.payload.data) {
          // New API structure: { success: true, data: { transactions: [...], chain: "...", coin: "..." } }
          if (action.payload.data.transactions) {
            state.transactionHistory.transactions = action.payload.data.transactions;
            state.transactionHistory.address = action.payload.data.address || '';
            state.transactionHistory.chain = action.payload.data.chain || '';
            state.transactionHistory.pagination = action.payload.pagination || {
              page: 1,
              limit: 10,
              totalRecords: action.payload.data.transactions.length,
              totalPages: 1
            };
          } else {
            // Fallback: data is the transactions array directly
            state.transactionHistory.transactions = action.payload.data;
            state.transactionHistory.address = '';
            state.transactionHistory.chain = '';
            state.transactionHistory.pagination = action.payload.pagination;
          }
        } else if (action.payload.transactions) {
          // Direct transactions array
          state.transactionHistory.transactions = action.payload.transactions;
          state.transactionHistory.pagination = {
            page: 1,
            limit: 10,
            totalRecords: action.payload.transactions.length,
            totalPages: 1
          };
        } else {
          // Fallback to empty
          state.transactionHistory.transactions = [];
          state.transactionHistory.pagination = {
            page: 1,
            limit: 10,
            totalRecords: 0,
            totalPages: 0
          };
        }
      })
      .addCase(getTransactionHistory.rejected, (state, action) => {
        state.transactionHistory.loading = false;
        state.transactionHistory.error = action.payload?.error || action.payload?.message || action.error?.message || 'Failed to fetch transaction history';
        state.transactionHistory.transactions = [];
      });
  }
});

export const { setloading, clearShieldError, clearUnshieldError, clearPrivateTransferError, clearShieldEstimate, clearUnshieldEstimate, clearApproveError, resetApproveState, clearShieldTxHash, clearUnshieldTxHash } = walletSlice.actions;

export default walletSlice.reducer;
